/* @HEADER
 * @version 1.1.943 | 2026-10-26
 * @purpose AuthenticatedApp's in-app navigation as one hook — the active space and view, the back-stack, the pending scroll to restore, and the four handlers (navigate, select space, switch view, go back) — over the decisions in hostNavigationDecisions.mjs (ZVX-DEV-6015).
 * @sidecar useHostNavigation.ts.header.md
 * @layer app | @hex adapter | @ctx navigation
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7676, ZVX-DEV-7685, ZVX-DEV-6015, ZVX-DEV-1454, ZVX-DEV-1616, ZVX-DEV-7775

import { useCallback, useRef, useState } from 'react'
import type { MutableRefObject } from 'react'
import { DEFAULT_VIEW } from '../app/routes'
import type { Route, SpaceView } from '../app/routes'
import { navigateRoute } from './useAuthRouteState'
import type { NavVia } from './useBoardChangeTell'
// @ts-expect-error — framework-free .mjs decisions, typed by their JSDoc
import { shouldRecordNavigate, snapshotOf, pushSnapshot, popSnapshot } from './hostNavigationDecisions.mjs'

export type NavSnapshot = { route: Route; spaceId: string | null; view: SpaceView; scrollTop: number }

export interface UseHostNavigationDeps {
  /** The URL-hash route in force (useAuthRouteState). */
  route: Route
  /** How far the main column is scrolled right now — captured into a snapshot before a move. */
  captureScroll: () => number
}

/** A card a link asked to open (ZVX-DEV-8332): its sync id and the board the link names. */
export interface OpenCardRequest {
  syncId: string
  boardId: string | null
}

export interface HostNavigation {
  activeSpaceId: string | null
  /** `via` is how the person got there, told to the recorder (ZVX-DEV-7676); `other` when the caller does not say. */
  setActiveSpaceId: (id: string | null, via?: NavVia) => void
  /** ZVX-DEV-7676: the way recorded for the move that just happened (`other` when nobody said). */
  takeNavVia: () => NavVia
  activeView: SpaceView
  setActiveView: (view: SpaceView) => void
  /** ZVX-DEV-7685: a card to highlight as the space opens (a card found in another space); the page clears it once taken. */
  searchHighlightOnOpen: string | null
  setSearchHighlightOnOpen: (taskId: string | null) => void
  /**
   * ZVX-DEV-8332 (ZVX-4645): the card a link in the address asked to open, by its sync id and the board the link names (`null`: any board of
   * the space). Set when the link lands; the space page clears it with `onOpenCardOnArrivalTaken` once it has opened the card or given up.
   */
  openCardOnArrival: OpenCardRequest | null
  setOpenCardOnArrival: (request: OpenCardRequest | null) => void
  onOpenCardOnArrivalTaken: () => void
  /** ZVX-DEV-7775: the card about to arrive is not a search's (`null` clears the mark); the page then records nothing for it. */
  markQuietArrival: (taskId: string | null) => void
  /**
   * ZVX-DEV-7775: what the space page calls when it has taken the card handed to it: the hand-over is cleared, and
   * the answer is `false` for a card marked quiet (the card window's button -- not a search, not recorded), `true`
   * otherwise. The mark is spent either way.
   */
  onSearchHighlightOnOpenTaken: (taskId: string) => boolean
  canGoBack: boolean
  /** Written before back-navigation; read by AppShell's useLayoutEffect to restore scroll. */
  pendingScrollRef: MutableRefObject<number | null>
  handleNavigate: (r: Route) => void
  handleSelectSpace: (spaceId: string) => void
  handleSwitchView: (view: SpaceView) => void
  handleGoBack: () => void
}

/**
 * ZVX-DEV-6015 (campaign 3, App.tsx) — the navigation state machine that
 * AuthenticatedApp carried inline since ZVX-DEV-1454. The URL hash stays the
 * source of truth for the route (`navigateRoute` writes it; the caller's
 * `useAuthRouteState` reads it back); space and view are intra-tasks state.
 */
export function useHostNavigation({ route, captureScroll }: UseHostNavigationDeps): HostNavigation {
  const [activeSpaceId, setActiveSpaceIdState] = useState<string | null>(null)
  // ZVX-DEV-7676: who is moving the person. Every move into a space says it again (`other` when the caller is silent),
  // so a word left by a click on the space already on screen is overwritten by the next move.
  const viaRef = useRef<NavVia>('other')
  const setActiveSpaceId = useCallback((id: string | null, via: NavVia = 'other') => {
    if (id) viaRef.current = via
    setActiveSpaceIdState(id)
  }, [])
  const takeNavVia = useCallback((): NavVia => viaRef.current, [])
  const [activeView, setActiveView] = useState<SpaceView>(DEFAULT_VIEW)
  const [searchHighlightOnOpen, setSearchHighlightOnOpen] = useState<string | null>(null)
  const [openCardOnArrival, setOpenCardOnArrival] = useState<OpenCardRequest | null>(null)
  const onOpenCardOnArrivalTaken = useCallback(() => setOpenCardOnArrival(null), [])
  const quietArrivalRef = useRef<string | null>(null)
  const markQuietArrival = useCallback((taskId: string | null) => { quietArrivalRef.current = taskId }, [])
  const onSearchHighlightOnOpenTaken = useCallback((taskId: string): boolean => {
    const quiet = quietArrivalRef.current === taskId
    quietArrivalRef.current = null
    setSearchHighlightOnOpen(null)
    return !quiet
  }, [])
  const [navHistory, setNavHistory] = useState<NavSnapshot[]>([])
  const pendingScrollRef = useRef<number | null>(null)

  const record = useCallback(() => {
    const snap = snapshotOf({ route, activeSpaceId, activeView, scrollTop: captureScroll() }) as NavSnapshot
    setNavHistory((prev) => pushSnapshot(prev, snap))
  }, [route, activeSpaceId, activeView, captureScroll])

  /** Navigate to a global route (events, diary, admin, etc.) */
  const handleNavigate = useCallback((r: Route) => {
    if (shouldRecordNavigate({ route, target: r, activeSpaceId })) record()
    // ZVX-DEV-1454 — write to URL hash so route is shareable/bookmarkable.
    navigateRoute(r)
    if (r === 'tasks') {
      setActiveSpaceId(null)
      setActiveView('kanban')
    }
  }, [route, activeSpaceId, record, setActiveSpaceId])

  /** Enter a specific space */
  const handleSelectSpace = useCallback((spaceId: string) => {
    record()
    navigateRoute('tasks')
    setActiveSpaceId(spaceId, 'click')
    setActiveView('kanban')
  }, [record, setActiveSpaceId])

  /** Switch view within current space */
  const handleSwitchView = useCallback((view: SpaceView) => {
    if (view !== activeView) record()
    setActiveView(view)
  }, [activeView, record])

  /** Go back to the previous navigation state */
  const handleGoBack = useCallback(() => {
    setNavHistory((prev) => {
      const popped = popSnapshot(prev) as { snap: NavSnapshot; rest: NavSnapshot[] } | null
      if (!popped) return prev
      pendingScrollRef.current = popped.snap.scrollTop
      // ZVX-DEV-1454 — restore the route via URL so back-nav also updates the
      // address bar; the hashchange listener picks it up on the next render.
      navigateRoute(popped.snap.route)
      setActiveSpaceId(popped.snap.spaceId, 'back')
      setActiveView(popped.snap.view)
      return popped.rest
    })
  }, [setActiveSpaceId])

  return {
    activeSpaceId, setActiveSpaceId, takeNavVia, activeView, setActiveView,
    searchHighlightOnOpen, setSearchHighlightOnOpen, markQuietArrival, onSearchHighlightOnOpenTaken,
    openCardOnArrival, setOpenCardOnArrival, onOpenCardOnArrivalTaken,
    canGoBack: navHistory.length > 0, pendingScrollRef,
    handleNavigate, handleSelectSpace, handleSwitchView, handleGoBack,
  }
}
